// Call search mode — the main front door for UAT troubleshooting.
//
// Fetches contactAIAgentSession for a date window (single API call, then
// client-side ANI filter — the WxCC search API doesn't accept an ANI arg;
// see reference_wxcc_search_api.md). Row click opens the unified
// interactionId-keyed CallDetail.
//
// Phase 5 additions:
//   - Concern score column + sort toggle (Time | Concern).
//   - Scored rows surface likely-troubled calls to the top in UAT.

import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Code,
  Grid,
  Group,
  Loader,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconSearch } from '@tabler/icons-react';

import { searchAiSessions } from '../../api_scripts/wxccSearchClient';
import { ErrorAlert, OutcomeBadge, fmtDuration, fmtTs, short, toDatetimeLocal } from './utils';
import { concernColor, concernLabel, scoreCall } from './concernScore';

export function CallSearch({ setView }) {
  const nowIso = toDatetimeLocal(new Date());
  const weekAgoIso = toDatetimeLocal(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));

  const [form, setForm] = useState({ from: weekAgoIso, to: nowIso, ani: '', agentName: '' });
  const [state, setState] = useState({ loading: false, rows: null, error: null, fetchedAt: null });
  const [sortMode, setSortMode] = useState('time');

  const runSearch = useCallback(async () => {
    const fromMs = new Date(form.from).getTime();
    const toMs = new Date(form.to).getTime();
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs >= toMs) {
      setState({ loading: false, rows: null, error: 'Pick a valid from/to window (from must be before to).', fetchedAt: null });
      return;
    }
    setState({ loading: true, rows: null, error: null, fetchedAt: null });
    try {
      const { rows, truncated, rawSessions } = await searchAiSessions({
        from: fromMs,
        to: toMs,
        aniContains: form.ani.trim() || undefined,
        returnRaw: true,
      });
      const nameFilter = form.agentName.trim().toLowerCase();
      const filtered = nameFilter
        ? rows.filter((r) => (r.agentName ?? '').toLowerCase().includes(nameFilter))
        : rows;
      // Compute concern once per row — downstream sort toggles between
      // startTs and concern without recomputing.
      const scored = filtered.map((r) => ({ ...r, concern: scoreCall(r) }));
      setState({
        loading: false,
        rows: scored,
        error: null,
        fetchedAt: Date.now(),
        truncated,
        window: { from: fromMs, to: toMs },
        rawSessions,
      });
    } catch (e) {
      setState({ loading: false, rows: null, error: e.message, fetchedAt: null });
    }
  }, [form]);

  const sortedRows = useMemo(() => {
    if (!state.rows) return null;
    const copy = [...state.rows];
    if (sortMode === 'concern') {
      copy.sort((a, b) => {
        const diff = (b.concern?.score ?? 0) - (a.concern?.score ?? 0);
        return diff !== 0 ? diff : (b.startTs ?? 0) - (a.startTs ?? 0);
      });
    } else {
      copy.sort((a, b) => (b.startTs ?? 0) - (a.startTs ?? 0));
    }
    return copy;
  }, [state.rows, sortMode]);

  return (
    <Stack>
      <div>
        <Title order={3}>Call search</Title>
        <Text size="sm" c="dimmed">
          Search AI Agent sessions by date range, then optionally narrow by ANI or agent name.
          The WxCC search API only filters by date server-side; ANI/agent-name filtering runs
          in the browser after the fetch, so keep the window as tight as your investigation allows.
        </Text>
      </div>

      <Paper withBorder p="md" radius="md">
        <Grid gutter="sm" align="flex-end">
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <TextInput
              label="From"
              type="datetime-local"
              value={form.from}
              onChange={(e) => setForm((f) => ({ ...f, from: e.currentTarget.value }))}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <TextInput
              label="To"
              type="datetime-local"
              value={form.to}
              onChange={(e) => setForm((f) => ({ ...f, to: e.currentTarget.value }))}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <TextInput
              label="ANI contains"
              placeholder="e.g. 8642639685"
              value={form.ani}
              onChange={(e) => setForm((f) => ({ ...f, ani: e.currentTarget.value }))}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <TextInput
              label="Agent name contains"
              placeholder="e.g. AfterVisit"
              value={form.agentName}
              onChange={(e) => setForm((f) => ({ ...f, agentName: e.currentTarget.value }))}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Group justify="space-between">
              <Group gap="xs">
                <Button
                  size="xs"
                  leftSection={<IconSearch size={14} />}
                  loading={state.loading}
                  onClick={runSearch}
                >
                  Search
                </Button>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => {
                    const nowLocal = toDatetimeLocal(new Date());
                    const dayAgoLocal = toDatetimeLocal(new Date(Date.now() - 24 * 60 * 60 * 1000));
                    setForm({ from: dayAgoLocal, to: nowLocal, ani: '', agentName: '' });
                  }}
                >
                  Last 24h
                </Button>
              </Group>
              {state.fetchedAt && (
                <Text size="xs" c="dimmed">
                  {state.rows?.length ?? 0} result{state.rows?.length === 1 ? '' : 's'} · fetched {fmtTs(state.fetchedAt, true)}
                </Text>
              )}
            </Group>
          </Grid.Col>
        </Grid>
      </Paper>

      {state.error && <ErrorAlert message={state.error} />}
      {state.loading && <Loader size="sm" />}

      {state.truncated && (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Results may be truncated">
          The API reported <Code>hasNextPage: true</Code> for this window — you're likely missing
          calls. Narrow the date range and search again.
        </Alert>
      )}

      {!state.loading && state.rows && state.rows.length === 0 && (
        <Alert color="gray" title="No sessions found">
          Try widening the date range, clearing the ANI/agent-name filters, or confirming this
          tenant has AI Agent traffic in the window.
        </Alert>
      )}

      {!state.loading && sortedRows && sortedRows.length > 0 && (
        <>
          <Group justify="flex-end" gap="xs">
            <Text size="xs" c="dimmed">Sort by</Text>
            <SegmentedControl
              size="xs"
              value={sortMode}
              onChange={setSortMode}
              data={[
                { label: 'Time', value: 'time' },
                { label: 'Concern', value: 'concern' },
              ]}
            />
          </Group>

          <Paper withBorder radius="md">
            <ScrollArea>
              <Table verticalSpacing="xs" highlightOnHover striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Concern</Table.Th>
                    <Table.Th>Started</Table.Th>
                    <Table.Th>ANI</Table.Th>
                    <Table.Th>DNIS</Table.Th>
                    <Table.Th>Agent</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Entry point</Table.Th>
                    <Table.Th>Outcome</Table.Th>
                    <Table.Th>Duration</Table.Th>
                    <Table.Th>Msgs</Table.Th>
                    <Table.Th>Interaction</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sortedRows.map((r) => (
                    <Table.Tr
                      key={`${r.interactionId}:${r.sessionId}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        setView({
                          name: 'call-detail',
                          interactionId: r.interactionId,
                          window: state.window,
                          cachedAiSessions: state.rawSessions,
                        })
                      }
                    >
                      <Table.Td>
                        <ConcernCell concern={r.concern} />
                      </Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtTs(r.startTs)}</Table.Td>
                      <Table.Td style={{ fontFamily: 'monospace' }}>{r.ani || '—'}</Table.Td>
                      <Table.Td style={{ fontFamily: 'monospace' }}>{r.dnis || '—'}</Table.Td>
                      <Table.Td>{r.agentName || '—'}</Table.Td>
                      <Table.Td>
                        {r.agentType && (
                          <Badge size="xs" variant="light" color={r.agentType === 'AUTONOMOUS' ? 'blue' : 'grape'}>
                            {r.agentType}
                          </Badge>
                        )}
                        {r.channelType && (
                          <Badge size="xs" variant="outline" color="gray" ml={4}>
                            {r.channelType}
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>{r.entryPoint || '—'}</Table.Td>
                      <Table.Td>
                        <OutcomeBadge outcome={r.outcome} />
                      </Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDuration(r.durationMs)}</Table.Td>
                      <Table.Td>{r.messageCount ?? '—'}</Table.Td>
                      <Table.Td>
                        <Code style={{ fontSize: 10 }}>{short(r.interactionId)}</Code>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>
        </>
      )}
    </Stack>
  );
}

function ConcernCell({ concern }) {
  const score = concern?.score ?? 0;
  const factors = concern?.factors ?? [];
  const color = concernColor(score);
  const label = concernLabel(score);
  const tooltip = factors.length
    ? factors.map((f) => `• ${f.label} (+${f.points})`).join('\n')
    : 'No concern signals detected';
  return (
    <Tooltip
      multiline
      withArrow
      maw={320}
      label={<pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap' }}>{tooltip}</pre>}
    >
      <Badge size="sm" color={color} variant={score >= 15 ? 'filled' : 'light'}>
        {score} {label !== 'none' ? label : ''}
      </Badge>
    </Tooltip>
  );
}
